import { buildCanonicalUrl, canonicalOriginForTenant, canonicalPathForLocation, ensureSingleCanonicalLink, removeCanonicalLinks } from './canonicalUrls.js';
import { getSettingValue } from './homepageHeroSettings.js';
import { safeJsonLd, buildOrganizationSchema, buildWebSiteSchema, composePublicSchemaGraph } from './structuredData.js';

const PLATFORM_NAME = 'My Town Spotlight';
const DEFAULT_LOCALE = 'en_US';
const OWNED = 'data-mts-public-metadata';

export function plainText(value = '') {
  return String(value || '')
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

function titleCaseSlug(value = '') {
  return decodeURIComponent(String(value || '')).split(/[-_]+/).filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
}

function validHttpsUrl(value, origin = '') {
  if (!value) return '';
  try {
    const url = new URL(String(value || ''), origin || undefined);
    return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : '';
  } catch {
    return '';
  }
}

export function tenantMetadataDefaults(tenant, context = {}) {
  const siteName = plainText(context.siteName || getSettingValue(tenant, 'seo.site_name') || getSettingValue(tenant, 'tenant.name') || tenant?.name || PLATFORM_NAME);
  const cityName = plainText(context.cityName || getSettingValue(tenant, 'tenant.city') || siteName.replace(/\s+spotlight$/i, '') || 'My Town');
  const origin = canonicalOriginForTenant(tenant);
  const defaultDescription = plainText(
    getSettingValue(tenant, 'seo.default_description')
      || `${siteName} connects ${cityName} with local businesses, deals, events, community stories, and trusted experts.`,
  );
  const configuredImage = getSettingValue(tenant, 'seo.social_image_url')
    || getSettingValue(tenant, 'website.social_image_url');
  const imageUrl = validHttpsUrl(configuredImage, origin) || validHttpsUrl('/social-default.png', origin);
  const logoUrl = validHttpsUrl(context.logoUrl || getSettingValue(tenant, 'tenant.logo_url') || getSettingValue(tenant, 'branding.logo_custom_url'), origin);
  return {
    siteName,
    cityName,
    origin,
    locale: plainText(getSettingValue(tenant, 'seo.locale') || DEFAULT_LOCALE),
    defaultTitle: plainText(getSettingValue(tenant, 'seo.default_title') || `${siteName} | Local Businesses, Deals & Events`),
    defaultDescription,
    imageUrl,
    logoUrl,
    imageAlt: `${siteName} community guide`,
    organizationDescription: plainText(getSettingValue(tenant, 'seo.organization_description') || defaultDescription),
    socialProfiles: (Array.isArray(getSettingValue(tenant, 'seo.social_profiles')) ? getSettingValue(tenant, 'seo.social_profiles') : []).map((value) => validHttpsUrl(value)).filter(Boolean),
  };
}

function routeTemplate(pathname, defaults) {
  const { siteName, cityName, defaultTitle, defaultDescription } = defaults;
  const segments = pathname.split('/').filter(Boolean);
  if (pathname === '/') return { title: defaultTitle, description: defaultDescription, schemaType: 'WebPage' };
  if (pathname === '/listings') return { title: `${cityName} Business Directory | ${siteName}`, description: `Explore local businesses serving ${cityName}. Find contact details, services, offers, and community recommendations.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'listings') return { title: `${titleCaseSlug(segments.at(-1))} in ${cityName} | ${siteName}`, description: `View business details, contact information, services, and local updates for ${titleCaseSlug(segments.at(-1))} in ${cityName}.`, schemaType: 'WebPage' };
  if (pathname === '/offers') return { title: `${cityName} Deals & Local Offers | ${siteName}`, description: `Discover current deals and special offers from local businesses in ${cityName}.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'offers' && segments[1] === 'category') return { title: `${titleCaseSlug(segments[2])} Deals in ${cityName} | ${siteName}`, description: `Browse ${titleCaseSlug(segments[2]).toLowerCase()} deals and local offers available around ${cityName}.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'offers') return { title: `${titleCaseSlug(segments.at(-1))} | ${siteName}`, description: `View this local offer available in ${cityName}, including details and redemption information.`, schemaType: 'WebPage' };
  if (pathname === '/events') return { title: `${cityName} Events & Community Calendar | ${siteName}`, description: `Find upcoming events, activities, and community happenings in ${cityName}.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'events') return { title: `${titleCaseSlug(segments.at(-1))} in ${cityName} | ${siteName}`, description: `View event details, date, time, and location for this community event in ${cityName}.`, schemaType: 'WebPage' };
  if (pathname === '/blog') return { title: `${cityName} Local News & Guides | ${siteName}`, description: `Read local stories, business spotlights, community news, and helpful guides from ${cityName}.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'blog' && segments[1] === 'category') return { title: `${titleCaseSlug(segments[2])} | ${cityName} Stories`, description: `Read ${titleCaseSlug(segments[2]).toLowerCase()} stories, news, and guides from ${cityName}.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'blog') return { title: `${titleCaseSlug(segments.at(-1))} | ${siteName}`, description: `Read this local story from ${siteName}, serving the ${cityName} community.`, schemaType: 'WebPage', openGraphType: 'article' };
  if (pathname === '/experts') return { title: `${cityName} Local Experts | ${siteName}`, description: `Meet trusted local experts and professionals serving ${cityName}.`, schemaType: 'CollectionPage' };
  if (segments[0] === 'experts') return { title: `${titleCaseSlug(segments.at(-1))}, Local Expert in ${cityName} | ${siteName}`, description: `Learn about ${titleCaseSlug(segments.at(-1))}, a local expert serving the ${cityName} community.`, schemaType: 'ProfilePage' };
  const staticPages = {
    '/about': [`About ${siteName}`, `Learn about ${siteName} and how we connect residents with local businesses and community resources in ${cityName}.`],
    '/contact': [`Contact ${siteName}`, `Contact ${siteName} for questions, local recommendations, advertising information, or community support in ${cityName}.`],
    '/advertise/spotlight': [`Advertise Your Business in ${cityName} | ${siteName}`, `Promote your business to local customers in ${cityName} with a featured directory presence from ${siteName}.`],
    '/advertise/mailer': [`${cityName} Shared Mail Advertising | ${siteName}`, `Reach households across ${cityName} with affordable shared mail advertising from ${siteName}.`],
    '/privacy-policy': [`Privacy Policy | ${siteName}`, `Read the privacy policy for ${siteName}.`],
    '/terms-of-use': [`Terms of Use | ${siteName}`, `Read the terms that govern use of ${siteName}.`],
    '/cookie-policy': [`Cookie Policy | ${siteName}`, `Learn how ${siteName} uses cookies and similar technologies.`],
  };
  if (staticPages[pathname]) return { title: staticPages[pathname][0], description: staticPages[pathname][1], schemaType: 'WebPage' };
  return { title: `${titleCaseSlug(segments.at(-1) || 'Page')} | ${siteName}`, description: defaultDescription, schemaType: 'WebPage' };
}

export function buildRouteMetadata({ tenant, pathname = '/', search = '', siteName, cityName, logoUrl }) {
  const defaults = tenantMetadataDefaults(tenant, { siteName, cityName, logoUrl });
  const canonicalPath = canonicalPathForLocation(pathname, search);
  const indexable = Boolean(canonicalPath) && tenant?.publicIndexingAllowed !== false;
  const pagePath = canonicalPath || pathname;
  const url = buildCanonicalUrl(tenant, pagePath);
  const template = routeTemplate(pathname, defaults);
  const jsonLd = indexable && url ? [{
    '@context': 'https://schema.org', '@type': template.schemaType, '@id': `${url}#webpage`,
    url, name: template.title, description: template.description,
    isPartOf: { '@id': `${defaults.origin}/#website` },
  }] : [];
  const siteJsonLd = defaults.origin ? [
    buildOrganizationSchema({ origin: defaults.origin, name: defaults.siteName, description: defaults.organizationDescription, logo: defaults.logoUrl, sameAs: defaults.socialProfiles }),
    buildWebSiteSchema({ origin: defaults.origin, name: defaults.siteName }),
  ] : [];

  return {
    title: plainText(template.title), description: plainText(template.description), canonicalUrl: indexable ? url : '',
    robots: indexable ? 'index,follow' : 'noindex,follow',
    openGraph: { type: template.openGraphType || 'website', title: plainText(template.title), description: plainText(template.description), url, imageUrl: defaults.imageUrl, imageAlt: defaults.imageAlt, siteName: defaults.siteName, locale: defaults.locale },
    twitter: { card: 'summary_large_image', title: plainText(template.title), description: plainText(template.description), imageUrl: defaults.imageUrl, imageAlt: defaults.imageAlt },
    jsonLd, siteJsonLd,
  };
}

function restrictIndexing(robots = 'follow') {
  const directives = [...new Set(String(robots).split(',').map(value => value.trim()).filter(value => value && !['index', 'noindex'].includes(value.toLowerCase())))];
  return ['noindex', ...directives].join(',');
}

export function buildPublicPageMetadata(input) {
  const base = buildRouteMetadata(input);
  const defaults = tenantMetadataDefaults(input.tenant, input);
  const canonicalUrl = input.canonicalPath ? buildCanonicalUrl(input.tenant, input.canonicalPath) : base.canonicalUrl;
  const title = plainText(input.title || base.title);
  const description = plainText(input.description || base.description);
  const imageUrl = validHttpsUrl(input.imageUrl, defaults.origin) || base.openGraph.imageUrl;
  const imageAlt = plainText(input.imageAlt || (input.title ? `${input.title} — ${defaults.siteName}` : base.openGraph.imageAlt));
  const allowed = Boolean(canonicalPathForLocation(input.pathname || '/', input.search || '')) && input.tenant?.publicIndexingAllowed !== false;
  const robots = allowed ? input.robots || base.robots : restrictIndexing(input.robots || base.robots);
  return {
    title, description, canonicalUrl: /\bnoindex\b/i.test(robots) ? '' : canonicalUrl, robots,
    openGraph: { type: input.openGraphType || base.openGraph.type, title, description, url: canonicalUrl || base.openGraph.url, imageUrl, imageAlt, siteName: defaults.siteName, locale: defaults.locale },
    twitter: { card: imageUrl ? 'summary_large_image' : 'summary', title, description, imageUrl, imageAlt },
    jsonLd: input.jsonLd ?? base.jsonLd, siteJsonLd: base.siteJsonLd,
  };
}

// A page factory may choose a more restrictive policy or a stable category alias,
// but cannot discard restrictions from the actual requested URL.
export function tightenMetadataForLocation(metadata, { pathname, search = '' }) {
  if (!metadata || (canonicalPathForLocation(pathname, search) && metadata.canonicalUrl && !/\bnoindex\b/i.test(metadata.robots || ''))) return metadata;
  return { ...metadata, canonicalUrl: '', robots: restrictIndexing(metadata.robots || 'follow'), jsonLd: [] };
}

function appendMeta(documentObject, selectorAttributes, content) {
  const node = documentObject.createElement('meta');
  Object.entries(selectorAttributes).forEach(([key, value]) => node.setAttribute(key, value));
  node.setAttribute('content', content || '');
  node.setAttribute(OWNED, 'true');
  documentObject.head.appendChild(node);
}

export function removePublicMetadata(documentObject = document) {
  documentObject.head.querySelectorAll(`[${OWNED}]`).forEach((node) => node.remove());
}

export function applyPublicMetadata(metadata, documentObject = document) {
  removePublicMetadata(documentObject);
  const singletonSelectors = [
    'meta[name="description"]', 'meta[name="robots"]',
    'meta[property="og:title"]', 'meta[property="og:description"]', 'meta[property="og:type"]',
    'meta[property="og:url"]', 'meta[property="og:image"]', 'meta[property="og:image:alt"]',
    'meta[property="og:site_name"]', 'meta[property="og:locale"]',
    'meta[name="twitter:card"]', 'meta[name="twitter:title"]', 'meta[name="twitter:description"]',
    'meta[name="twitter:image"]', 'meta[name="twitter:image:alt"]',
  ];
  singletonSelectors.forEach((selector) => documentObject.head.querySelectorAll(selector).forEach((node) => node.remove()));
  removeCanonicalLinks(documentObject);
  documentObject.title = plainText(metadata.title);
  appendMeta(documentObject, { name: 'description' }, plainText(metadata.description));
  appendMeta(documentObject, { name: 'robots' }, metadata.robots || 'index,follow');
  const og = metadata.openGraph || {};
  [['title', og.title], ['description', og.description], ['type', og.type], ['url', og.url], ['image', og.imageUrl], ['image:alt', og.imageAlt], ['site_name', og.siteName], ['locale', og.locale]].forEach(([property, value]) => {
    if (value) appendMeta(documentObject, { property: `og:${property}` }, String(value));
  });
  const twitter = metadata.twitter || {};
  [['card', twitter.card], ['title', twitter.title], ['description', twitter.description], ['image', twitter.imageUrl], ['image:alt', twitter.imageAlt]].forEach(([name, value]) => {
    if (value) appendMeta(documentObject, { name: `twitter:${name}` }, String(value));
  });
  if (metadata.canonicalUrl) {
    const canonical = ensureSingleCanonicalLink(metadata.canonicalUrl, documentObject);
    canonical.setAttribute(OWNED, 'true');
  }
  composePublicSchemaGraph(metadata).forEach((value) => {
    const script = documentObject.createElement('script');
    script.type = 'application/ld+json';
    script.setAttribute(OWNED, 'true');
    script.textContent = safeJsonLd(value);
    documentObject.head.appendChild(script);
  });
  return metadata;
}

// Pure server serialization of the same metadata contract used by DOM updates.
export function renderPublicMetadataHtml(metadata) {
  const escape = value => String(value ?? '').replace(/[&<>"']/g, char => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[char]);
  const tags = [`<title>${escape(plainText(metadata.title))}</title>`];
  const meta = (attribute, key, value) => {
    if (value) tags.push(`<meta ${attribute}="${escape(key)}" content="${escape(value)}">`);
  };
  tags.push(`<meta name="description" content="${escape(plainText(metadata.description))}">`);
  meta('name', 'robots', metadata.robots || 'index,follow');
  const og = metadata.openGraph || {};
  for (const [key, value] of [['title', og.title], ['description', og.description], ['type', og.type], ['url', og.url], ['image', og.imageUrl], ['image:alt', og.imageAlt], ['site_name', og.siteName], ['locale', og.locale]]) meta('property', `og:${key}`, value);
  const twitter = metadata.twitter || {};
  for (const [key, value] of [['card', twitter.card], ['title', twitter.title], ['description', twitter.description], ['image', twitter.imageUrl], ['image:alt', twitter.imageAlt]]) meta('name', `twitter:${key}`, value);
  if (metadata.canonicalUrl) tags.push(`<link rel="canonical" href="${escape(metadata.canonicalUrl)}">`);
  for (const schema of composePublicSchemaGraph(metadata)) tags.push(`<script type="application/ld+json">${safeJsonLd(schema)}</script>`);
  return `<html><head>${tags.join('')}</head></html>`;
}
